import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Orange vs blue, deliberately. The two series have to stay distinguishable for
// red-green colour blindness, and orange/green — the obvious pick from the accuracy
// tier palette — is the single worst pair for it.
const CONFIDENCE_COLOR = '#f97316';
const ACCURACY_COLOR = '#38bdf8';

function formatDay(isoStr) {
  const [year, month, day] = isoStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function TopicChart({ topicName, series }) {
  const formatted = series.map(d => ({
    label: formatDay(d.day),
    confidence: d.confidence,
    accuracy: d.accuracy,
  }));

  // A one-point line is not a line: with dot={false} recharts emits a bare moveto ("M370,64Z")
  // which strokes nothing, so the accuracy series would be invisible on the day a topic gets
  // its first answer — i.e. on every new user's first visit, which is exactly who this chart
  // is for. Recharts' own single-point dot fallback does not fire here, because the points
  // array is the full window (nulls included), so its length is never 1. Draw a dot instead.
  const accuracyPoints = formatted.filter(d => d.accuracy !== null).length;
  const accuracyDot = accuracyPoints === 1
    ? { r: 3, fill: ACCURACY_COLOR, stroke: '#080a12', strokeWidth: 2 }
    : false;

  return (
    <div className="progress-chart-card">
      <p className="progress-chart-title">{topicName}</p>
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={formatted} margin={{ top: 8, right: 0, left: -28, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis
            dataKey="label"
            tick={{ fill: '#475569', fontSize: 10, fontFamily: 'Outfit,sans-serif' }}
            axisLine={false}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            yAxisId="confidence"
            tick={{ fill: '#475569', fontSize: 10, fontFamily: 'JetBrains Mono,monospace' }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <YAxis
            yAxisId="accuracy"
            orientation="right"
            domain={[0, 100]}
            ticks={[0, 50, 100]}
            width={30}
            tick={{ fill: '#475569', fontSize: 10, fontFamily: 'JetBrains Mono,monospace' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{ background: '#0d1117', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', fontSize: '12px', fontFamily: 'JetBrains Mono,monospace', color: '#f1f5f9' }}
            cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
            formatter={(value, name) => [name === 'Accuracy' ? `${value}%` : value, name]}
          />
          <Legend
            verticalAlign="top"
            align="right"
            height={20}
            iconType="plainline"
            iconSize={12}
            wrapperStyle={{ fontSize: '10px', fontFamily: 'Outfit,sans-serif' }}
          />
          <Line
            yAxisId="confidence"
            type="monotone"
            dataKey="confidence"
            name="Confidence"
            stroke={CONFIDENCE_COLOR}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: CONFIDENCE_COLOR, stroke: '#080a12', strokeWidth: 2 }}
          />
          {/* connectNulls={false}: accuracy is null until the first answer lands, and a line
              drawn across that gap would invent a score the user never earned. */}
          <Line
            yAxisId="accuracy"
            type="monotone"
            dataKey="accuracy"
            name="Accuracy"
            stroke={ACCURACY_COLOR}
            strokeWidth={2}
            dot={accuracyDot}
            connectNulls={false}
            activeDot={{ r: 4, fill: ACCURACY_COLOR, stroke: '#080a12', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ProgressHistoryChart({ topics }) {
  if (!topics || topics.length === 0) return null;
  return (
    <div className="progress-charts-grid">
      {topics.map(t => <TopicChart key={t.topic_id} topicName={t.topic_name} series={t.series} />)}
    </div>
  );
}
